import React, { useEffect, useRef, useState } from 'react';
import '../Css/Reviewsection.css';
// --- NEW IMPORTS ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';

export default function Reviewsection() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);
  const reviewsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const autoScrollRef = useRef(null);
  
  // --- NEW STATE FOR IMAGE ERRORS ---
  const [imageErrors, setImageErrors] = useState({});

  // Review data
  const reviews = [
    {
      name: "Jassat Esmail",
      location: "UK",
      image: null, // This will now have a fallback
      text: "Albert Knowledge: Studies has been involved in the development of a new technology that helps us to develop and improve our skills. Team working with interactive teams plays."
    },
    {
      name: "Sam Bernstein",
      location: "Australia",
      image: null,
      text: "I've gone here from a real pleasure to work with, your communication and service has been really good considering the time change and geographical location. We are interested to do more work with you."
    },
    // Example of a review with no image path provided
    {
      name: "Diallo",
      location: "United States",
      image: null, // This will also trigger the fallback
      text: "I talked about my career as an important player. I learned how many people experience the better life among adults. Drawing important messages with 24/30 conversations is online."
    }
  ];

  // --- NEW: STYLE OBJECT FOR FALLBACK ICON ---
  const fallbackStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '3rem' // Adjust size as needed
  };

  // --- NEW: HANDLER FOR IMAGE ONERROR EVENT ---
  const handleImageError = (index) => {
    setImageErrors(prevErrors => ({
      ...prevErrors,
      [index]: true
    }));
  };

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (headerRef.current) {
              headerRef.current.classList.add('animate');
            }
            cardsRef.current.forEach((card) => {
              if (card) {
                card.classList.add('animate');
              }
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentSection = sectionRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // Auto scroll for mobile and tablet
  useEffect(() => {
    if (!isMobile && !isTablet) return;

    const startAutoScroll = () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }

      autoScrollRef.current = setInterval(() => {
        const totalCards = reviews.length;
        const nextIndex = (activeIndex + 1) % totalCards;
        scrollToCard(nextIndex);
      }, 4000);
    };

    startAutoScroll();

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [activeIndex, isMobile, isTablet, reviews.length]);

  // Track scroll position on mobile and tablet
  useEffect(() => {
    if ((!isMobile && !isTablet) || !reviewsRef.current) return;

    const handleScroll = () => {
      const container = reviewsRef.current;
      const scrollLeft = container.scrollLeft;
      const cards = container.querySelectorAll('.review-card');
      
      if (cards.length > 0) {
        const cardWidth = cards[0].offsetWidth;
        const gap = 20;
        const index = Math.round(scrollLeft / (cardWidth + gap));
        setActiveIndex(index);
      }
    };

    const container = reviewsRef.current;
    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMobile, isTablet]);

  // NEW: Track active card for large screens
  useEffect(() => {
    if (isMobile || isTablet || !reviewsRef.current) return;

    const handleLargeScreenScroll = () => {
      const container = reviewsRef.current;
      if (!container) return;
      
      const cards = container.querySelectorAll('.review-card');
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      
      let closestCardIndex = 0;
      let minDistance = Infinity;
      
      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestCardIndex = index;
        }
      });
      
      setActiveIndex(closestCardIndex);
    };

    const container = reviewsRef.current;
    container.addEventListener('scroll', handleLargeScreenScroll);
    
    // Initial calculation
    handleLargeScreenScroll();

    return () => container.removeEventListener('scroll', handleLargeScreenScroll);
  }, [isMobile, isTablet]);

  const scrollToCard = (index) => {
    if (!reviewsRef.current) return;
    
    const container = reviewsRef.current;
    const cards = container.querySelectorAll('.review-card');
    
    if (cards[index]) {
      const cardWidth = cards[index].offsetWidth;
      const gap = 20;
      const scrollPosition = index * (cardWidth + gap);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  const handleDotClick = (index) => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }
    scrollToCard(index);
    
    // Restart auto-scroll for mobile/tablet only
    if (isMobile || isTablet) {
      setTimeout(() => {
        autoScrollRef.current = setInterval(() => {
          const totalCards = reviews.length;
          const nextIndex = (activeIndex + 1) % totalCards;
          scrollToCard(nextIndex);
        }, 4000);
      }, 5000);
    }
  };

  return (
    <div className="review-section" ref={sectionRef}>
      <div className="review-container">
        <div className="review-header" ref={headerRef}>
          <h2 className="main-heading">Client Testimonials</h2>
          <h2 className="sub-heading">What Say About Us</h2>
          <p className="description">
            We take step wise for your every platform. we helping to client
            <br />
            with our talented expert.
          </p>
        </div>
      </div>

      <div className="reviews-wrapper">
        <div 
          className={`reviews ${isMobile || isTablet ? 'scrollable' : 'large-screen-scrollable'}`} 
          ref={reviewsRef}
        >
          {reviews.map((review, index) => (
            <div 
              className={`review-card card-${index + 1}`}
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
            >
              {/* --- UPDATED IMAGE LOGIC --- */}
              {(imageErrors[index] || !review.image) ? (
                <div 
                  className="client-photo client-photo-fallback" 
                  style={fallbackStyle}
                >
                  <FontAwesomeIcon icon={faUser} style={{
                    height : '50%',
                    width : '50%'
                  }}/>
                </div>
              ) : (
                <img 
                  src={review.image} 
                  alt={review.name} 
                  className="client-photo"
                  onError={() => handleImageError(index)}
                />
              )}
              {/* --- END UPDATED LOGIC --- */}
              
              <h4>{review.name}</h4>
              <p className="client-location">{review.location}</p>
              <img src='/images/blue-quote.png' alt='quote-icon' className="quote-icon"/>
              <p className="review-text">{review.text}</p>
            </div>
          ))}
        </div>

        {/* REMOVED CONDITIONAL RENDERING - ALWAYS SHOW DOTS */}
        <div className="dots-container">
          {reviews.map((_, index) => (
            <button
              key={index}
              className={`dot ${activeIndex === index ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}